import { __decorate } from "tslib";
import { lightTheme } from '@getgo/chameleon-core';
import { FASTElement, attr, customElement, html, DOM } from '@microsoft/fast-element';
import { debounce } from 'lodash-es';
import { ensureDefaultAttribute } from '../common/utils/Attributes';
import { ChameleonElementMixin } from '../common/ChameleonElement';
import { assertSlotNonNull } from '../common/asserts';
import { getTabbableChildren } from '../common/utils/DomUtil';
import { calculatePopoverCoordinates } from './calculatePopoverCoordinates';
import { calculateCaretPositionCSSProperties } from './calculateCaretPositionCSSProperties';
import { PopoverTriggerOn } from './model';
import styles from './Popover.styles.scss';
import { createPopoverTriggerStrategy } from './strategies/PopoverTriggerStrategyFactory';
export const POPOVER_DEFAULT_WIDTH = +lightTheme['menu-width'].replace('px', '');
const DEFAULT_POSITION = `top-center`;
const DEFAULT_Z_INDEX = 1;
export const popoverTemplate = html `
  <slot name="trigger"></slot>
  <div
    :className="${(x) => (x.hidden ? 'hidden' : x.width >= 300 ? 'popover large' : 'popover')}"
    :style="${(x) => `
      width: ${x.width && x.width > 0 ? `${x.width}px` : 'inherit'};
      min-width: ${x.minWidth ? `${x.minWidth}px` : 'inherit'};
      max-width: ${x.maxWidth ? `${x.maxWidth}px` : 'inherit'};
      z-index: ${x.zIndex}
    `}"
    part="popover"
  >
    <slot name="content"></slot>
  </div>
`;
export const popoverDefinition = {
    name: 'chameleon-popover',
    template: popoverTemplate,
    styles,
};
let PopoverComponent = class PopoverComponent extends ChameleonElementMixin(FASTElement) {
    constructor() {
        super();
        this.width = POPOVER_DEFAULT_WIDTH;
        this.compact = false;
        this.inverted = false;
        this.hidden = false;
        this.triggerOn = PopoverTriggerOn.click;
        this.positionObserverHandler = () => {
            this.updatePosition();
        };
        this.debouncePositionObserverHandler = debounce(this.positionObserverHandler, 50);
        this.zIndex = DEFAULT_Z_INDEX;
        this.isOpen = false;
        this.handleScrollEvent = () => {
            this.updatePosition();
        };
        this.handleMenuItemClick = (e) => {
            if (!e.defaultPrevented) {
                this.close();
            }
        };
        this.positionObserver = new MutationObserver(this.positionObserverHandler);
    }
    widthChanged(_oldValue, newValue) {
        if (typeof newValue === 'undefined') {
            this.width = POPOVER_DEFAULT_WIDTH;
        }
        else {
            this.width = newValue;
        }
    }
    zIndexChanged(_oldValue, newValue) {
        if (newValue) {
            this.zIndex = newValue;
        }
        else {
            this.zIndex = DEFAULT_Z_INDEX;
        }
    }
    isOpenChanged(oldValue, newValue) {
        if (this.$fastController.isConnected) {
            DOM.queueUpdate(() => {
                if (newValue !== oldValue && newValue) {
                    this.open();
                }
                else {
                    this.close();
                }
            });
        }
    }
    connectedCallback() {
        var _a;
        super.connectedCallback();
        this.strategy = createPopoverTriggerStrategy(this);
        this.popoverElement = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.popover');
        this.assignContentElement();
        ensureDefaultAttribute(this, 'position', DEFAULT_POSITION);
        this.strategy.init();
        this.bindEvents();
        if (this.isOpen) {
            DOM.queueUpdate(() => {
                this.open();
            });
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.strategy.dispose();
        this.positionObserver.disconnect();
        this.debouncePositionObserverHandler.cancel();
        this.removeEventListener('menuitemclick', this.handleMenuItemClick);
        window.removeEventListener('resize', this.debouncePositionObserverHandler);
        this.removeEventListener('keydown', this.handleKeydownEvent);
    }
    isVisible() {
        return this.popoverElement.classList.contains('visible');
    }
    open() {
        if (!this.isVisible()) {
            this.isOpen = true;
            this.updatePosition();
            this.popoverElement.classList.add('visible');
            const tabbableChildren = getTabbableChildren(this.contentElement);
            if (tabbableChildren.length) {
                tabbableChildren[0].focus();
            }
            this.$emit('popoverchange', { isOpen: true }, { bubbles: false });
            this.strategy.onOpen();
            document.addEventListener('scroll', this.handleScrollEvent, { capture: true });
            this.addEventListener('keydown', this.handleKeydownEvent);
        }
    }
    close() {
        if (this.isVisible()) {
            this.isOpen = false;
            this.popoverElement.classList.remove('visible');
            this.$emit('popoverchange', { isOpen: false }, { bubbles: false });
            this.strategy.onClose();
            document.removeEventListener('scroll', this.handleScrollEvent);
            this.removeEventListener('keydown', this.handleKeydownEvent);
            // Only forward back focus to trigger if popover has been opened by
            // keyboard navigation
            if (this.strategy.hasBeenTriggeredBy === 'keyboard') {
                this.strategy.trigger.focus();
            }
            this.strategy.hasBeenTriggeredBy = null;
        }
    }
    bindEvents() {
        this.addEventListener('menuitemclick', this.handleMenuItemClick);
        window.addEventListener('resize', this.debouncePositionObserverHandler);
        this.positionObserver.observe(this.contentElement, {
            characterData: true,
            subtree: true,
            childList: true,
            attributes: true,
        });
    }
    getBounds() {
        return {
            position: this.getPositionAttribute(),
            triggerBounds: this.getTriggerBounds(),
            contentBounds: this.popoverElement.getBoundingClientRect(),
        };
    }
    getPositionAttribute() {
        return this.getAttribute('position');
    }
    getTriggerBounds() {
        return this.strategy.trigger.getBoundingClientRect();
    }
    updatePosition() {
        const computedPosition = calculatePopoverCoordinates(this.getBounds());
        if (computedPosition) {
            this.popoverElement.style.top = `${computedPosition.content.y}px`;
            this.popoverElement.style.left = `${computedPosition.content.x}px`;
            this.setAttribute('caret', computedPosition.position);
            const caretProperties = calculateCaretPositionCSSProperties(computedPosition);
            Object.keys(caretProperties).forEach((key) => {
                this.popoverElement.style.setProperty(key, caretProperties[key]);
            });
        }
    }
    handleKeydownEvent(e) {
        if (e.key === 'Escape') {
            this.close();
        }
    }
    assignContentElement() {
        this.contentElement = this.querySelector('[slot="content"]');
        assertSlotNonNull(this.contentElement, 'PopoverComponent', 'content');
        if (this.contentElement.tagName.toLowerCase() === 'chameleon-menu') {
            throw new Error(`Chameleon error: chameleon-menu cannot be a direct child of PopoverComponent. Please wrap it with another tag, such as div`);
        }
    }
};
__decorate([
    attr
], PopoverComponent.prototype, "width", void 0);
__decorate([
    attr({ attribute: 'min-width' })
], PopoverComponent.prototype, "minWidth", void 0);
__decorate([
    attr({ attribute: 'max-width' })
], PopoverComponent.prototype, "maxWidth", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverComponent.prototype, "compact", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverComponent.prototype, "inverted", void 0);
__decorate([
    attr({ mode: 'boolean' })
], PopoverComponent.prototype, "hidden", void 0);
__decorate([
    attr({ attribute: 'trigger-on' })
], PopoverComponent.prototype, "triggerOn", void 0);
__decorate([
    attr({ attribute: 'z-index' })
], PopoverComponent.prototype, "zIndex", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'is-open' })
], PopoverComponent.prototype, "isOpen", void 0);
PopoverComponent = __decorate([
    customElement(popoverDefinition)
], PopoverComponent);
export { PopoverComponent };
